import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import useContentProtection from '../../hooks/useContentProtection';
import ReactMarkdown from 'react-markdown';
import { 
  BookOpen, 
  ArrowLeft,
  FileText,
  ChevronRight,
  ChevronLeft,
  Layers,
  Download
} from 'lucide-react';
import DOMPurify from 'dompurify';

// ReactMarkdown component overrides - defined outside component to avoid recreation on each render
const markdownComponents = {
  h1: ({children}) => <h1 className="text-2xl font-bold mt-6 mb-4">{children}</h1>,
  h2: ({children}) => <h2 className="text-xl font-semibold mt-5 mb-3">{children}</h2>,
  h3: ({children}) => <h3 className="text-lg font-medium mt-4 mb-2">{children}</h3>,
  p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
  ul: ({children}) => <ul className="list-disc ml-6 mb-4 space-y-1">{children}</ul>,
  ol: ({children}) => <ol className="list-decimal ml-6 mb-4 space-y-1">{children}</ol>,
  li: ({children}) => <li className="mb-1">{children}</li>,
  strong: ({children}) => <strong className="font-semibold">{children}</strong>,
  em: ({children}) => <em className="italic">{children}</em>,
  a: ({href, children}) => <a href={href} className="text-primary underline hover:no-underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  blockquote: ({children}) => <blockquote className="border-l-4 border-primary/30 pl-4 italic my-4">{children}</blockquote>,
  code: ({children}) => <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
  pre: ({children}) => <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4">{children}</pre>,
};

const RPATPlaybookDetail = () => {
  const { playbookId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [playbook, setPlaybook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [error, setError] = useState(null);

  const fetchPlaybook = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/rpat/playbooks/${playbookId}`);
      setPlaybook(res.data);
      // Auto-select first chapter
      if (res.data.chapters?.length > 0) {
        setSelectedChapter(res.data.chapters[0]);
      }
    } catch (err) {
      console.error('Failed to fetch playbook:', err);
      setError(err.response?.data?.detail || 'Failed to load playbook');
    } finally {
      setLoading(false);
    }
  }, [api, playbookId]);

  useEffect(() => {
    fetchPlaybook();
  }, [fetchPlaybook]);

  const handleChapterSelect = (chapter) => {
    setSelectedChapter(chapter);
  };

  const handleNextChapter = () => {
    if (!playbook?.chapters || !selectedChapter) return;
    const currentIndex = playbook.chapters.findIndex(c => c.id === selectedChapter.id);
    if (currentIndex < playbook.chapters.length - 1) {
      setSelectedChapter(playbook.chapters[currentIndex + 1]);
    }
  };

  const handlePrevChapter = () => {
    if (!playbook?.chapters || !selectedChapter) return;
    const currentIndex = playbook.chapters.findIndex(c => c.id === selectedChapter.id);
    if (currentIndex > 0) {
      setSelectedChapter(playbook.chapters[currentIndex - 1]);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <Skeleton className="h-96" />
            <Skeleton className="h-96 lg:col-span-3" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !playbook) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-6 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Playbook Not Found</h2>
              <p className="text-muted-foreground mb-4">{error || 'The requested playbook could not be found.'}</p>
              <Button onClick={() => navigate('/rpat/playbooks')}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Playbooks
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const currentChapterIndex = playbook.chapters?.findIndex(c => c.id === selectedChapter?.id) ?? -1;
  const hasPrev = currentChapterIndex > 0;
  const hasNext = currentChapterIndex < (playbook.chapters?.length ?? 0) - 1;

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/rpat/playbooks')} data-testid="back-to-playbooks">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{playbook.title}</h1>
            {playbook.description && (
              <p className="text-muted-foreground">{playbook.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              <FileText className="h-3 w-3 mr-1" />
              {playbook.total_pages || 0} pages
            </Badge>
            <Badge variant="secondary">
              <Layers className="h-3 w-3 mr-1" />
              {playbook.chapters?.length || 0} chapters
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Chapter List Sidebar */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Chapters</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-300px)]">
                <div className="p-2 space-y-1">
                  {playbook.chapters?.map((chapter, index) => (
                    <button
                      key={chapter.id}
                      onClick={() => handleChapterSelect(chapter)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedChapter?.id === chapter.id
                          ? 'bg-violet-100 text-violet-900 border border-violet-200'
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`chapter-${index + 1}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                          selectedChapter?.id === chapter.id
                            ? 'bg-violet-200 text-violet-800'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="text-sm font-medium line-clamp-2">
                          {chapter.title}
                        </span>
                      </div>
                      {chapter.page_count > 0 && (
                        <span className="text-xs text-muted-foreground mt-1 block pl-7">
                          {chapter.page_count} pages
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chapter Content */}
          <Card className="lg:col-span-3">
            {selectedChapter ? (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">
                        Chapter {currentChapterIndex + 1} of {playbook.chapters?.length}
                      </Badge>
                      <CardTitle>{selectedChapter.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[calc(100vh-400px)]">
                    <div className="p-6 prose prose-slate max-w-none dark:prose-invert">
                      {/* Prefer markdown if it has more content than HTML */}
                      {selectedChapter.content_markdown && 
                       (!selectedChapter.content_html || 
                        selectedChapter.content_markdown.length > (selectedChapter.content_html?.length || 0) * 2) ? (
                        <div className="leading-relaxed">
                          <ReactMarkdown components={markdownComponents}>
                            {selectedChapter.content_markdown}
                          </ReactMarkdown>
                        </div>
                      ) : selectedChapter.content_html ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedChapter.content_html) }} />
                      ) : (
                        <p className="text-muted-foreground text-center py-8">
                          No content available for this chapter.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                  
                  {/* Navigation */}
                  <div className="border-t p-4 flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={handlePrevChapter}
                      disabled={!hasPrev}
                      data-testid="prev-chapter"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    
                    <span className="text-sm text-muted-foreground">
                      {currentChapterIndex + 1} / {playbook.chapters?.length}
                    </span>
                    
                    <Button
                      variant="outline"
                      onClick={handleNextChapter}
                      disabled={!hasNext}
                      data-testid="next-chapter"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-96">
                <div className="text-center">
                  <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Select a chapter to begin reading</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default RPATPlaybookDetail;
